import React, { useRef, useState, useEffect } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import api from '../utils/api';

export default function SignatureCanvas({ label, value, onChange, required = false, disabled = false }) {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [fileObjectUrl, setFileObjectUrl] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Set white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Set drawing styles
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Load existing signature if provided
    if (value) {
      const loadSignature = async () => {
        try {
          // Value can be a File object or a string URL
          let sourceUrl;
          let shouldRevokeUrl = false;

          if (typeof value === 'object' && value instanceof File) {
            // File object - create object URL directly
            sourceUrl = URL.createObjectURL(value);
            shouldRevokeUrl = true;
          } else if (typeof value === 'string') {
            // String URL - could be a data URL or API endpoint
            // If it's an API endpoint (contains /api/), fetch with token
            if (value.includes('/api/')) {
              try {
                const res = await api.get(value);
                const blob = await res.blob();
                sourceUrl = URL.createObjectURL(blob);
                shouldRevokeUrl = true;
              } catch (err) {
                console.error('Failed to fetch signature from API:', err);
                return;
              }
            } else {
              // It's a data URL or public URL
              sourceUrl = value;
            }
          } else {
            return;
          }

          // Load the image
          const img = new Image();
          img.onload = () => {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            setHasSignature(true);
            
            // Clean up object URL if needed
            if (shouldRevokeUrl) {
              URL.revokeObjectURL(sourceUrl);
            }
          };
          img.onerror = () => {
            console.error('Failed to load signature image');
            if (shouldRevokeUrl) {
              URL.revokeObjectURL(sourceUrl);
            }
          };
          img.src = sourceUrl;
        } catch (err) {
          console.error('Error in loadSignature:', err);
        }
      };

      loadSignature();
    }
  }, [value]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 2MB for signatures)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size should be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          
          // Clear with white background
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw uploaded image to canvas
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Pass the File object to parent
          setHasSignature(true);
          setIsUploaded(true);
          if (onChange) onChange(file);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Handle both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    setIsDrawing(false);
    setHasSignature(true);
    
    // Convert canvas to Blob and create File object
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      const file = new File([blob], 'signature.jpg', { type: 'image/jpeg' });
      if (onChange) onChange(file);
    }, 'image/jpeg', 0.8);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear with white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    setHasSignature(false);
    setIsUploaded(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (onChange) onChange(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {hasSignature && (
            <IconButton size="small" onClick={clearSignature} color="error" disabled={disabled}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <IconButton size="small" onClick={() => fileInputRef.current?.click()} color="primary" disabled={disabled}>
            <UploadFileIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          border: '2px dashed',
          borderColor: hasSignature ? 'success.main' : required ? 'error.light' : 'grey.300',
          borderRadius: 1,
          p: 0,
          backgroundColor: disabled ? 'grey.200' : 'grey.50',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          touchAction: 'none',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <canvas
          ref={canvasRef}
          width={400}
          height={150}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
          onMouseDown={disabled ? undefined : startDrawing}
          onMouseMove={disabled ? undefined : draw}
          onMouseUp={disabled ? undefined : stopDrawing}
          onMouseLeave={disabled ? undefined : stopDrawing}
          onTouchStart={disabled ? undefined : startDrawing}
          onTouchMove={disabled ? undefined : draw}
          onTouchEnd={disabled ? undefined : stopDrawing}
        />
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
        {hasSignature ? (isUploaded ? 'Signature uploaded' : 'Signature captured') : 'Draw or upload signature'}
      </Typography>
    </Box>
  );
}
